import React, { useState, useEffect } from "react";
import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import {
  getDVIRQuestions,
  addQuestionToTemplateOptimistic,
} from "../../dvirSlice";
import { DVIRQuestion, DVIRQuestionType } from "../../../../models";
import "./AddTemplateQuestionModal.scss";

interface AddQuestionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  templateId: string;
  existingQuestionIds: string[];
}

const getQuestionTypeLabel = (type: DVIRQuestionType): string => {
  switch (type) {
    case "boolean":
      return "Boolean";
    case "text":
      return "Text";
    case "number":
      return "Number";
    default:
      return type;
  }
};

const getQuestionTypeColor = (type: DVIRQuestionType): string => {
  switch (type) {
    case "boolean":
      return "type-boolean";
    case "text":
      return "type-text";
    case "number":
      return "type-number";
    default:
      return "type-boolean";
  }
};

export function AddTemplateQuestionModal({
  isOpen,
  onClose,
  templateId,
  existingQuestionIds,
}: AddQuestionsModalProps) {
  const dispatch = useAppDispatch();

  const allQuestions = useAppSelector((state) => state.dvir.questions);
  const getDVIRQuestionsAPI = useAppSelector(
    (state) => state.dvir.getDVIRQuestions
  );
  const addQuestionAPI = useAppSelector(
    (state) => state.dvir.addQuestionToTemplate
  );

  const [selectedQuestionId, setSelectedQuestionId] = useState<string>("");
  const [isRequired, setIsRequired] = useState<boolean>(true);

  // Fetch questions when modal opens
  useEffect(() => {
    if (isOpen) {
      dispatch(getDVIRQuestions());
    }
  }, [isOpen, dispatch]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedQuestionId("");
      setIsRequired(true);
    }
  }, [isOpen]);

  // Close modal after successful addition
  useEffect(() => {
    if (!addQuestionAPI.loading && addQuestionAPI.value && selectedQuestionId) {
      onClose();
    }
  }, [
    addQuestionAPI.loading,
    addQuestionAPI.value,
    selectedQuestionId,
    onClose,
  ]);

  // Filter available questions (exclude questions already in template)
  const availableQuestions = allQuestions.filter(
    (q) => !existingQuestionIds.includes(q.id)
  );

  const selectedQuestion = allQuestions.find(
    (q) => q.id === selectedQuestionId
  );

  const handleAddQuestion = () => {
    if (!selectedQuestion) return;

    dispatch(
      addQuestionToTemplateOptimistic({
        templateId,
        questionId: selectedQuestion.id,
        isRequired,
      })
    );
  };

  const handleQuestionSelect = (question: DVIRQuestion) => {
    setSelectedQuestionId(question.id);
    // Auto-set required based on question type (booleans often required)
    setIsRequired(true);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="add-questions-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add Questions to Template</h3>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {/* Questions List */}
          <div className="questions-section">
            <div className="section-title">
              Available Questions ({availableQuestions.length})
            </div>

            {getDVIRQuestionsAPI.loading ? (
              <div className="loading-state">Loading questions...</div>
            ) : availableQuestions.length === 0 ? (
              <div className="empty-state">
                <p>All available questions are already in this template.</p>
              </div>
            ) : (
              <div className="questions-list">
                {availableQuestions.map((question) => (
                  <div
                    key={question.id}
                    className={`question-card ${
                      selectedQuestionId === question.id ? "selected" : ""
                    }`}
                    onClick={() => handleQuestionSelect(question)}
                  >
                    <div className="question-content">
                      <div className="question-text">
                        {question.questionText}
                      </div>
                      <div className="question-meta">
                        <span
                          className={`question-type-badge ${getQuestionTypeColor(
                            question.questionType
                          )}`}
                        >
                          {getQuestionTypeLabel(question.questionType)}
                        </span>
                        <span className="usage-count">
                          Used in {question.templateCount} template
                          {question.templateCount === 1 ? "" : "s"}
                        </span>
                      </div>
                    </div>
                    <div className="selection-indicator">
                      {selectedQuestionId === question.id && (
                        <div className="checkmark">✓</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Selected Question Configuration */}
          {selectedQuestion && (
            <div className="question-config">
              <div className="config-header">
                <h4>Question Configuration</h4>
              </div>
              <div className="config-content">
                <div className="selected-question">
                  <div className="question-preview">
                    "{selectedQuestion.questionText}"
                  </div>
                  <span
                    className={`question-type-badge ${getQuestionTypeColor(
                      selectedQuestion.questionType
                    )}`}
                  >
                    {getQuestionTypeLabel(selectedQuestion.questionType)}
                  </span>
                </div>
                <div className="config-option">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={isRequired}
                      onChange={(e) => setIsRequired(e.target.checked)}
                    />
                    <span>Mark as required question</span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={addQuestionAPI.loading}
          >
            Done
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleAddQuestion}
            disabled={!selectedQuestion || addQuestionAPI.loading}
          >
            {addQuestionAPI.loading ? "Adding..." : "Add Question"}
          </button>
        </div>
      </div>
    </div>
  );
}
